import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib';
import { getAttachmentDisplayLabel } from '../../lib/shortenAttachmentUrl';
import type { ConversationAttachmentResponse, PhraseAttachmentInPhraseResponse } from '../../model/api/types';

/**
 * Пропсы внутреннего компонента-ссылки {@link AttachmentLink}.
 */
type Props = {
  /** Вложение, чью ссылку отрисовываем. */
  attachment: ConversationAttachmentResponse | PhraseAttachmentInPhraseResponse;
  /** Идёт запрос подписанной ссылки — заблокировать кнопку. */
  isLoading: boolean;
  /** Открыть внутреннее вложение через подписанную ссылку. */
  onOpenInternal: () => void;
  /** Компактный режим — наследуем цвет текста родителя (для облака чата). */
  compact: boolean;
};

/**
 * Ссылка/кнопка открытия вложения.
 *
 * Для внешних ссылок — `<a target="_blank">`, для внутренних — `<button>`,
 * вызывающий запрос подписанной ссылки.
 */
export const AttachmentLink: FC<Props> = ({ attachment, isLoading, onOpenInternal, compact }) => {
  const { t } = useTranslation('dialogs');
  const labelClassName = 'block w-full truncate text-sm';
  // В облаке транскрипта фон — primary/secondary; используем currentColor родителя,
  // в табе вложений — стандартный акцентный цвет ссылки
  const linkColorClass = compact ? 'text-current' : 'text-primary';

  if (!attachment.url) {
    return <span className={cn(labelClassName, 'text-muted-foreground')}>{t('attachmentUnavailable')}</span>;
  }

  const displayLabel = getAttachmentDisplayLabel(attachment);

  if (attachment.isExternalUrl) {
    return (
      <a
        href={attachment.url}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(labelClassName, linkColorClass, 'hover:underline')}
        title={attachment.url}
      >
        {displayLabel}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpenInternal}
      disabled={isLoading}
      className={cn(
        labelClassName,
        linkColorClass,
        'text-left hover:underline disabled:cursor-progress disabled:opacity-60',
      )}
      title={attachment.url}
    >
      {displayLabel}
    </button>
  );
};
